// src/pages/TeamManagement.js
import { useEffect, useState } from 'react';
import {
  doc, getDoc, updateDoc, arrayUnion, arrayRemove, addDoc, collection,
  serverTimestamp, getDocs, query, where
} from 'firebase/firestore/lite';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import PageLoading from '../components/PageLoading';

export default function TeamManagement() {
  const { currentUser, userProfile, isImpersonating } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState(null);
  const [members, setMembers] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [addEmailFor, setAddEmailFor] = useState(null); // placeholder doc id being given an email
  const [addEmailValue, setAddEmailValue] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const teamId = userProfile?.teamId;

  useEffect(() => {
    // No teamId on the profile (e.g. admin, or previewing a team role) —
    // fall through to the "No team found" state instead of loading forever
    if (teamId) loadTeam();
    else setLoading(false);
  }, [teamId]);

  async function loadTeam() {
    try {
      const snap = await getDoc(doc(db, 'teams', teamId));
      if (snap.exists()) {
        const teamData = snap.data();
        let memberEmails = teamData.memberEmails || [];

        // Self-heal: the leader should always be a member. Older teams (or ones
        // where the leader was later removed) can have leaderEmail missing from
        // memberEmails, which silently drops them from every member list.
        const leaderEmail = teamData.leaderEmail;
        if (leaderEmail && !memberEmails.some(e => e.toLowerCase() === leaderEmail.toLowerCase())) {
          await updateDoc(doc(db, 'teams', teamId), { memberEmails: arrayUnion(leaderEmail) });
          memberEmails = [...memberEmails, leaderEmail];
        }

        setTeam({ id: snap.id, ...teamData, memberEmails });
        await Promise.all([
          loadMembers(memberEmails, leaderEmail),
          loadPlaceholders(teamData.pendingMemberIds || []),
        ]);
      }
    } catch (err) {
      console.error('Error loading team:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMembers(emails, leaderEmail) {
    if (!emails.length) { setMembers([]); return; }
    try {
      const usersSnap = await getDocs(
        query(collection(db, 'users'), where('email', 'in', emails.slice(0, 10)))
      );
      const userMap = {};
      usersSnap.forEach(d => { userMap[d.data().email] = { uid: d.id, ...d.data() }; });

      const responsesSnap = await getDocs(
        query(collection(db, 'responses'), where('teamId', '==', teamId))
      );
      const submittedUserIds = new Set(responsesSnap.docs.map(d => d.data().userId));

      const leaderKey = (leaderEmail || '').toLowerCase();
      const membersList = emails
        .map(email => {
          const user = userMap[email];
          const uid = user?.uid;
          return { email, registered: !!user, uid, name: user?.displayName || null, submitted: uid ? submittedUserIds.has(uid) : false, isLeader: email.toLowerCase() === leaderKey };
        })
        .sort((a, b) => {
          if (a.isLeader !== b.isLeader) return a.isLeader ? -1 : 1;
          return (a.name || a.email).toLowerCase().localeCompare((b.name || b.email).toLowerCase());
        });

      setMembers(membersList);
    } catch (err) {
      console.error('Error loading members:', err);
    }
  }

  async function loadPlaceholders(ids) {
    if (!ids.length) { setPlaceholders([]); return; }
    try {
      const docs = await Promise.all(ids.map(id => getDoc(doc(db, 'users', id))));
      const list = docs
        .filter(d => d.exists())
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.displayName || '').toLowerCase().localeCompare((b.displayName || '').toLowerCase()));
      setPlaceholders(list);
    } catch (err) {
      console.error('Error loading placeholders:', err);
    }
  }

  async function addMember() {
    if (isImpersonating || !newName.trim()) return;
    setInviting(true);
    setMessage(null);
    const name = newName.trim();
    const email = newEmail.trim().toLowerCase();

    try {
      if (email) {
        // Email provided — create invitation and send email
        const inviteRef = await addDoc(collection(db, 'invitations'), {
          email, role: 'team_member', teamId, teamName: team?.name,
          invitedBy: currentUser.uid, status: 'pending', createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'teams', teamId), { memberEmails: arrayUnion(email) });
        try {
          const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
          await sendInviteEmail({ to: email, role: 'team_member', teamName: team?.name, inviteToken: inviteRef.id, inviterName: userProfile?.displayName });
        } catch (emailErr) {
          console.warn('Email send failed:', emailErr.message);
        }
        setMessage({ type: 'success', text: `Invitation sent to ${email}` });
      } else {
        // Name only — create a placeholder user doc
        const placeholderRef = await addDoc(collection(db, 'users'), {
          displayName: name,
          email: null,
          role: 'team_member',
          teamId,
          isPlaceholder: true,
          createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'teams', teamId), { pendingMemberIds: arrayUnion(placeholderRef.id) });
        setMessage({ type: 'success', text: `${name} added to team.` });
      }
      setNewName('');
      setNewEmail('');
      await loadTeam();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to add member. Please try again.' });
    } finally {
      setInviting(false);
    }
  }

  async function sendInvite(email, role) {
    if (isImpersonating) return;
    email = (email || '').toLowerCase();
    setResendingEmail(email);
    setMessage(null);
    try {
      const inviteRef = await addDoc(collection(db, 'invitations'), {
        email, role, teamId, teamName: team?.name,
        invitedBy: currentUser.uid, status: 'pending', createdAt: serverTimestamp(),
      });
      if (role === 'team_member') {
        await updateDoc(doc(db, 'teams', teamId), { memberEmails: arrayUnion(email) });
      } else if (role === 'consultant') {
        await updateDoc(doc(db, 'teams', teamId), { consultantEmails: arrayUnion(email) });
      }
      try {
        const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
        await sendInviteEmail({ to: email, role, teamName: team?.name, inviteToken: inviteRef.id, inviterName: userProfile?.displayName });
      } catch (emailErr) {
        console.warn('Email send failed:', emailErr.message);
      }
      setMessage({ type: 'success', text: `Invite resent to ${email}` });
      await loadTeam();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to resend invite. Please try again.' });
    } finally {
      setResendingEmail(null);
    }
  }

  async function addEmailToPlaceholder(placeholderId, name) {
    if (isImpersonating) return;
    const email = addEmailValue.trim().toLowerCase();
    if (!email) return;
    setAddingEmail(true);
    setMessage(null);
    try {
      // Update the placeholder doc with the email
      await updateDoc(doc(db, 'users', placeholderId), { email, isPlaceholder: false });
      // Remove from pendingMemberIds, add to memberEmails
      await updateDoc(doc(db, 'teams', teamId), {
        pendingMemberIds: arrayRemove(placeholderId),
        memberEmails: arrayUnion(email),
      });
      // Send invite
      const inviteRef = await addDoc(collection(db, 'invitations'), {
        email, role: 'team_member', teamId, teamName: team?.name,
        invitedBy: currentUser.uid, status: 'pending', createdAt: serverTimestamp(),
      });
      try {
        const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
        await sendInviteEmail({ to: email, role: 'team_member', teamName: team?.name, inviteToken: inviteRef.id, inviterName: userProfile?.displayName });
      } catch (emailErr) {
        console.warn('Email send failed:', emailErr.message);
      }
      setMessage({ type: 'success', text: `Invitation sent to ${email}` });
      setAddEmailFor(null);
      setAddEmailValue('');
      await loadTeam();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to add email. Please try again.' });
    } finally {
      setAddingEmail(false);
    }
  }

  async function sendReminders() {
    if (isImpersonating) return;
    setSendingReminders(true);
    setMessage(null);
    try {
      const sendAssessmentReminder = httpsCallable(functions, 'sendAssessmentReminder');
      const result = await sendAssessmentReminder({ teamId });
      const sent = result.data?.sent ?? 0;
      setMessage({
        type: 'success',
        text: sent > 0 ? `Reminder sent to ${sent} member${sent === 1 ? '' : 's'}.` : 'Everyone has already completed the assessment.',
      });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to send reminders. Please try again.' });
    } finally {
      setSendingReminders(false);
    }
  }

  async function removeMember(email) {
    if (isImpersonating) return;
    setRemovingEmail(email);
    try {
      await updateDoc(doc(db, 'teams', teamId), { memberEmails: arrayRemove(email) });
      setMembers(prev => prev.filter(m => m.email !== email));
      setTeam(prev => ({ ...prev, memberEmails: (prev.memberEmails || []).filter(e => e !== email) }));
      setMessage({ type: 'success', text: `${email} removed from team.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to remove member.' });
    } finally {
      setRemovingEmail(null);
      setConfirmRemove(null);
    }
  }

  if (loading) return <PageLoading text="Loading team..." />;

  if (!team) return (
    <div className="fade-up">
      <div className="page-header">
        <h2>No team found</h2>
        <p>You haven't been assigned to a team yet. Contact your administrator.</p>
      </div>
    </div>
  );

  const submittedCount = members.filter(m => m.submitted).length;
  const registeredCount = members.filter(m => m.registered).length;
  const totalCount = members.length + placeholders.length;
  const pendingReminderCount = members.filter(m => m.registered && !m.submitted).length;

  return (
    <div className="fade-up">
      <div className="page-header" style={{ marginBottom: 32 }}>
        <h2>{team.name}</h2>
        <p>Manage your team's members and track assessment progress.</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 24 }}>
          {message.text}
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-label">Team Members</div>
          <div className="stat-value">{totalCount}</div>
          <div className="stat-sub">{registeredCount} registered</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Assessments</div>
          <div className="stat-value">{submittedCount}</div>
          <div className="stat-sub">of {members.length} completed</div>
          <button
            className="btn btn-secondary"
            style={{ marginTop: 12, padding: '6px 14px', fontSize: '0.8rem' }}
            onClick={sendReminders}
            disabled={sendingReminders || isImpersonating || pendingReminderCount === 0}
            title={isImpersonating ? 'Disabled while viewing as another user' : pendingReminderCount === 0 ? 'Everyone has completed the assessment' : undefined}
          >
            {sendingReminders ? <span className="spinner" /> : 'Send reminder'}
          </button>
        </div>
      </div>

      {/* Team Members List */}
      <div className="card" style={{ marginBottom: 32 }}>
        <div className="card-title">Team Members ({totalCount})</div>
        <div className="card-subtitle">Team leader shown first, then alphabetically — check mark indicates assessment completed</div>

        {totalCount === 0 ? (
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', marginTop: 16 }}>
            No members yet. Use the form below to add your team.
          </p>
        ) : (
          <div style={{ marginTop: 16 }}>
            {/* Column headers */}
            <div className="member-list-row" style={{
              gap: '8px 16px',
              padding: '6px 12px',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-muted)',
              borderBottom: '1px solid var(--border)',
              marginBottom: 8,
            }}>
              <span>Name</span>
              <span className="member-email-col">Email</span>
              <span style={{ textAlign: 'center' }}>Assessment</span>
              <span></span>
            </div>

            {/* Placeholder members (name only, no email) */}
            {placeholders.map(p => (
              <div
                key={p.id}
                className="member-list-row"
                style={{ gap: '8px 16px', alignItems: 'center', padding: '10px 12px', borderRadius: 8, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Name */}
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink)' }}>{p.displayName}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginTop: 2 }}>No email yet</div>
                </div>

                {/* Email / Add Email inline */}
                <div className="member-email-col">
                  {addEmailFor === p.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="email"
                        value={addEmailValue}
                        onChange={e => setAddEmailValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addEmailToPlaceholder(p.id, p.displayName)}
                        placeholder="email@company.com"
                        autoFocus
                        style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}
                      />
                      <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.8rem' }} onClick={() => addEmailToPlaceholder(p.id, p.displayName)} disabled={addingEmail || !addEmailValue || isImpersonating}>
                        {addingEmail ? '...' : 'Send'}
                      </button>
                      <button className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.8rem' }} onClick={() => { setAddEmailFor(null); setAddEmailValue(''); }}>✕</button>
                    </div>
                  ) : (
                    <button onClick={() => setAddEmailFor(p.id)} disabled={isImpersonating} title={isImpersonating ? 'Disabled while viewing as another user' : undefined} style={{ background: 'none', border: '1px dashed var(--border)', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: '0.78rem', padding: '4px 10px', borderRadius: 6 }}>
                      + Add email
                    </button>
                  )}
                </div>

                {/* Assessment — not applicable for placeholders */}
                <div style={{ textAlign: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: '2px solid var(--border)', borderRadius: '50%', color: 'var(--ink-muted)', fontSize: '0.75rem' }}>–</span>
                </div>

                {/* No remove for now — placeholder cleanup can be added later */}
                <div />
              </div>
            ))}

            {/* Registered / invited members */}
            {members.map(m => (
              <div
                key={m.email}
                className="member-list-row"
                style={{ gap: '8px 16px', alignItems: 'center', padding: '10px 12px', borderRadius: 8, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Name */}
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {m.name || <span style={{ color: 'var(--ink-muted)', fontStyle: 'italic', fontWeight: 400 }}>Not registered</span>}
                    {m.isLeader && (
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '1px 6px' }}>
                        Team Leader
                      </span>
                    )}
                  </div>
                  {!m.registered && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--gold)', marginTop: 2 }}>Invite pending</div>
                  )}
                  <div className="member-email-mobile" style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 2 }}>{m.email}</div>
                </div>

                {/* Email */}
                <div className="member-email-col" style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.email}
                </div>

                {/* Assessment checkmark */}
                <div style={{ textAlign: 'center' }}>
                  {m.submitted ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, background: 'var(--olive-light)', borderRadius: '50%', color: 'white', fontSize: '0.8rem', fontWeight: 700 }}>✓</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: '2px solid var(--border)', borderRadius: '50%', color: 'var(--ink-muted)', fontSize: '0.75rem' }}>–</span>
                  )}
                </div>

                {/* Resend invite / Remove buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {!m.registered && confirmRemove !== m.email && (
                    <button onClick={() => sendInvite(m.email, 'team_member')} disabled={resendingEmail === m.email || isImpersonating} title={isImpersonating ? 'Disabled while viewing as another user' : 'Resend invite email'} style={{ background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: '0.8rem', padding: '4px 8px', borderRadius: 6, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-muted)'}>
                      {resendingEmail === m.email ? '...' : 'Resend invite'}
                    </button>
                  )}
                  {confirmRemove === m.email ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => removeMember(m.email)} disabled={removingEmail === m.email || isImpersonating}>
                        {removingEmail === m.email ? '...' : 'Confirm'}
                      </button>
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setConfirmRemove(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRemove(m.email)} disabled={isImpersonating} title={isImpersonating ? 'Disabled while viewing as another user' : 'Remove member'} style={{ background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: '0.8rem', padding: '4px 8px', borderRadius: 6, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--burgundy)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-muted)'}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Team Members Section */}
      <div className="card">
        <div className="card-title">Add Team Members</div>
        <div className="card-subtitle">Name is required. Add an email to send an invite — or leave it blank to add them now and invite later.</div>

        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && newName.trim() && addMember()}
            placeholder="Full name (required)"
            style={{ flex: '2 1 160px', padding: '12px 16px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: '0.95rem' }}
          />
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && newName.trim() && addMember()}
            placeholder="Email (optional)"
            style={{ flex: '3 1 200px', padding: '12px 16px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: '0.95rem' }}
          />
          <button className="btn btn-primary" onClick={addMember} disabled={inviting || !newName.trim() || isImpersonating} title={isImpersonating ? 'Disabled while viewing as another user' : undefined}>
            {inviting ? <span className="spinner" /> : newEmail.trim() ? 'Add & invite →' : 'Add member →'}
          </button>
        </div>
      </div>
    </div>
  );
}
